import React, { Suspense } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import Heading from '@atoms/Heading';
import HeadingSize from '@atoms/Heading/sizes';
import ResultsRow from '@atoms/ResultsRow';
import GrayContainer from '@atoms/GrayContainer';
import ToggleButtons from '@molecules/ToggleButtons';
import { ButtonProps, ButtonType } from '@atoms/Button';

import useBreakpoint from 'hooks/useBreakpoint';
import useResults from 'hooks/useResults';
import useImagesState from 'hooks/useImagesState';

import { ResultsUnitSystem } from 'enums/calculator';

import styles from './styles';

const LazyComponent = React.lazy(() => import('./LazyPdfDownloadLink'));

const Step5 = () => {
  const { t } = useTranslation();
  const {
    isMobile,
  } = useBreakpoint();

  const results = useResults();
  const {
    packDimensions,
    dimensionsUnit,
    palletDimensions,
    totalDimensions,
    totalWeightString,
    heightString,
    unitsPerLayer,
    totalLayers,
    packagesString,
    overhang,
    surfaceUsage,
    resultsUnitSystem,
    loadDimensions,
    setResultsUnitSystem,
  } = results;

  const {
    width: packageWidth,
    length: packageLength,
    height: packageHeight,
  } = packDimensions;

  const {
    width: loadWidth = 0,
    length: loadLength = 0,
    height: loadHeight = 0,
  } = loadDimensions;

  const {
    width: palletWidth = 0,
    length: palletLength = 0,
    height: palletHeight = 0,
  } = palletDimensions;

  const {
    width: totalWidth = 0,
    length: totalLength = 0,
    height: totalHeight = 0,
  } = totalDimensions;

  const widthAcronym = t('widthAcronym');
  const heightAcronym = t('heightAcronym');
  const lengthAcronym = t('lengthAcronym');

  const dimensionInfo = (width: number | null, length: number | null, height: number | null) => (
    <Trans
      values={{
        width,
        length,
        height,
        widthAcronym,
        heightAcronym,
        lengthAcronym,
        unit: dimensionsUnit.toLowerCase(),
      }}
      components={{ span: <span /> }}
    >
      dimensionString
    </Trans>
  );

  const unitDimensionInfo = dimensionInfo(packageWidth, packageLength, packageHeight);
  const loadDimensionInfo = dimensionInfo(loadWidth, loadLength, loadHeight);
  const palletDimensionInfo = dimensionInfo(palletWidth, palletLength, palletHeight);
  const totalDimensionInfo = dimensionInfo(totalWidth, totalLength, totalHeight);

  const { i2d, i3d } = useImagesState();
  const image3D = i3d && <img src={i3d} alt={t('PT Systems and Automation')} />;

  const toggle: ButtonProps[] = [
    {
      type: ButtonType.PILL,
      children: ResultsUnitSystem.IMPERIAL,
      active: resultsUnitSystem === ResultsUnitSystem.IMPERIAL,
      className: styles.toggleButton1,
    },
    {
      type: ButtonType.PILL,
      children: ResultsUnitSystem.METRIC,
      active: resultsUnitSystem === ResultsUnitSystem.METRIC,
      className: styles.toggleButton2,
    },
  ];

  const resultsUnitOnChange = (item: ButtonProps) => setResultsUnitSystem(
    item.children as ResultsUnitSystem,
  );

  const overhangLabel = (
    <React.Fragment>
      Overhang:
      <br />
      (per side)
    </React.Fragment>
  );

  const reportPdfName = `${t('reportPdfName')}.pdf`;
  const fallbackText = t('Loading document...');

  const buttonsSection = (
    <Suspense fallback={fallbackText}>
      <LazyComponent data={results} pdfName={reportPdfName} image2D={i2d} image3D={i3d} />
    </Suspense>
  );

  return (
    <section className="md:flex justify-between md:gap-12 text-lg">
      <section className="user-input md:w-2/5">
        <section id="results">
          <Heading size={HeadingSize.H1}>
            {t('home:step5.title')}
          </Heading>
          <div className={styles.toggleSection}>
            <Heading className="flex items-center mt-10">
              Measurement system
            </Heading>
            <ToggleButtons
              className={styles.toggle}
              items={toggle}
              onChange={resultsUnitOnChange}
            />
          </div>
          <div className="results">
            <ResultsRow label="Unit Dimensions:" value={unitDimensionInfo} />
            <ResultsRow label="Load Dimensions:" value={loadDimensionInfo} />
            <ResultsRow label="Pallet Dimensions:" value={palletDimensionInfo} />
            <ResultsRow label="Total Dimensions:" value={totalDimensionInfo} />
            <ResultsRow label="Total Weight:" value={totalWeightString} />
            <ResultsRow label="Total Height:" value={heightString} />
            <ResultsRow label="# units per layer:" value={unitsPerLayer} />
            <ResultsRow label="# of layers:" value={totalLayers} />
            <ResultsRow label="Total number of packages:" value={packagesString} />
            <ResultsRow label="Surface Usage:" value={surfaceUsage} />
            <ResultsRow label={overhangLabel} value={overhang} />
          </div>
        </section>
        {!isMobile && buttonsSection}
      </section>
      <section className="my-14 md:my-0 md:w-3/5">
        <GrayContainer>
          {image3D}
        </GrayContainer>
        <small className="block text-darkGray text-xs mt-5 leading-[18px]">
          <p className="!mb-0">The rendering is theoretical and may not accurately reflect reality.</p>
          <p className="!mb-0">We make no warranties or representations as to its accuracy</p>
        </small>
      </section>
      {isMobile && buttonsSection}
    </section>
  );
};

export default Step5;
